/* eslint-disable arrow-body-style */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Bubble, Segment } from 'tea-component';
import GameImage from '@/pages/home/components/GameImage';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import SegmentAutoSlider from '../../../PCGames/components/SliderSegement/SegmentAutoSlider';

import type { IBaseProps, IGameType } from '@intelligence/types/common';

import './index.scss';

export interface IDownloadableContent {
  name: string;
  image: string;
  time: string;
}

interface IDownloadableContentProps extends IBaseProps {
  entityType: IGameType;
  downloadContent: {
    [key: string]: IDownloadableContent[];
  };
  segmentsSortFn?: Function;
}

export default function DownloadableContent(props: IDownloadableContentProps) {
  const { isRelease, entityType, downloadContent: downloadContents, segmentsSortFn } = props;

  const emptyTipsLocaleKey = isRelease
    ? 'dbi_detail_downloadable_content_nodata_tips'
    : 'dbi_detail_game_not_launch_tips';

  const { formatMessage } = useIntl();

  const groupsContainer = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => {
    const groups = Object.keys(downloadContents || {}).map((item) => {
      const length = downloadContents?.[item]?.length;
      // 中文时键名位 'zh|en' 名称拼接，英文时无 "|" 拼接，值都取 split 后的第一项
      const text = item?.split('|')?.[0];
      return {
        text: length > 0 ? `${text}(${length})` : text,
        value: item,
      };
    });

    // segments 需根据固定指标顺序排序
    if (typeof segmentsSortFn === 'function') {
      return segmentsSortFn(groups);
    }

    return groups;
  }, [downloadContents]);
  const [group, setGroup] = useState('');

  const handleGroupChange = (payload: string) => {
    setGroup(payload);
  };

  useEffect(() => {
    setGroup((prev) => {
      // 兼容中英文拼接的键名
      if (prev.includes('|')) {
        const value = prev.split('|')?.[1];
        const currentValue = groups?.find((item: { value: string }) => item.value === value)?.value;
        if (currentValue) return currentValue;
      } else {
        const currentValue = groups?.find(
          (item: { value: string }) => item.value?.split('|')?.[1] === prev,
        )?.value;
        if (currentValue) return currentValue;
      }

      return groups?.[0]?.value || '';
    });
  }, [groups]);

  if (!groups?.length)
    return (
      <div className="dbi-downloadable-no-data">{formatMessage({ id: emptyTipsLocaleKey })}</div>
    );

  return (
    <>
      {groups?.length > 1 && (
        <div ref={groupsContainer} className="dbi-down-loadable-box">
          <SegmentAutoSlider allowBtnDisable getParentNode={() => groupsContainer.current}>
            <Segment options={groups} value={group} onChange={handleGroupChange} />
          </SegmentAutoSlider>
        </div>
      )}

      <div
        className={classnames(`dbi-downloadable-content`, { 'no-segment': groups?.length <= 1 })}
      >
        {Array.isArray(downloadContents?.[group]) && downloadContents?.[group].length > 0 ? (
          downloadContents?.[group]?.map((downloadContent, index) => (
            <CartContext key={index} entityType={entityType} data={downloadContent} />
          ))
        ) : (
          <div className="dbi-downloadable-no-data">
            {formatMessage({ id: emptyTipsLocaleKey })}
          </div>
        )}
      </div>
    </>
  );
}

const CartContext = (props: any) => {
  const { entityType, data } = props || {};
  const name = data?.name || '';
  const isPCConsoleCombine = entityType === PC_CONSOLE_COMBINE_ENTITY_TYPE;
  const imgEntityType = isPCConsoleCombine ? 'console' : entityType;

  return (
    <div className="dbi-downloadable-card">
      <GameImage
        entityType={imgEntityType}
        src={data?.image}
        className={classnames({
          'dbi-downloadable-card-img': isPCConsoleCombine || entityType === 'pc',
          'dbi-downloadable-card-img-combine': isPCConsoleCombine,
        })}
      />
      <div className="dbi-downloadable-card-right">
        <Bubble content={name} dark>
          <div className="downloadable-title">{name}</div>
        </Bubble>
        {data?.time && <div className="downloadable-flex-text">{data?.time}</div>}
      </div>
    </div>
  );
};
